import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Check, X, ExternalLink } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Combobox from "../components/Combobox";

export default function AdminProductRequestsPage() {
  const { user, ready } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/product-requests");
      setRequests(r.data.requests || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    setLoading(false);
  };
  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  const reject = async (id) => {
    if (!window.confirm("Reject this request?")) return;
    await api.post(`/admin/product-requests/${id}/reject`, { admin_notes: "" });
    toast.success("Rejected");
    load();
  };

  if (!ready) return <div className="max-w-7xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground underline">← Admin Console</Link>
      <div className="mt-2 text-xs uppercase tracking-[0.2em] font-bold text-brand">Admin</div>
      <h1 className="t-page flex items-center gap-3">
        <Plus size={28} /> Product Requests
      </h1>
      <p className="text-sm text-muted-foreground mt-1">User-submitted products waiting for review. Approve to publish, reject to discard.</p>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : requests.length === 0 ? (
        <div className="mt-8 border border-dashed border-border py-16 text-center" data-testid="product-requests-empty">
          <p className="text-muted-foreground">No product requests yet.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="border border-border rounded-none p-5 bg-card" data-testid={`product-request-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-heading font-bold text-lg">{r.product}</h3>
                  <div className="text-sm text-muted-foreground">{r.brand}</div>
                  {r.reason && <p className="text-xs mt-2 bg-foreground/5 border-l-2 border-[#FFBE06] p-2">"{r.reason}"</p>}
                  <div className="text-xs text-muted-foreground mt-2">
                    by <strong>{r.user_name || "Anonymous"}</strong> · +91 {r.user_phone} · {new Date(r.created_at).toLocaleString("en-IN")}
                  </div>
                </div>
                <span className={`bhega-pill ${
                  r.status === "approved" ? "bg-[var(--bhega-success-soft)] text-[var(--bhega-success)]" :
                  r.status === "rejected" ? "bg-[var(--bhega-error-soft)] text-[var(--bhega-error)]" :
                  "bg-yellow-100 text-yellow-800"
                }`}>{r.status}</span>
              </div>
              {r.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing(r)} className="btn-primary text-xs" data-testid={`pr-approve-${r.id}`}>
                    <Check size={12} /> Review & approve
                  </button>
                  <button onClick={() => reject(r.id)} className="btn-outline text-xs" data-testid={`pr-reject-${r.id}`}>
                    <X size={12} /> Reject
                  </button>
                </div>
              )}
              {r.status === "approved" && r.product_id && (
                <Link to={`/browse?q=${encodeURIComponent(r.product || r.brand || "")}`} className="text-xs mt-3 inline-flex items-center gap-1 underline">
                  View published product <ExternalLink size={12} />
                </Link>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && <ApproveModal request={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ApproveModal({ request, onClose, onDone }) {
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [primary, setPrimary] = useState("");
  const [variants, setVariants] = useState("");
  const [website, setWebsite] = useState("");
  const [cities, setCities] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  const submit = async () => {
    if (!category) { toast.error("Pick or create a category"); return; }
    setBusy(true);
    try {
      const body = {
        category,
        primary_tags: primary.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6),
        variants_tags: variants.split(",").map((s) => s.trim()).filter(Boolean),
        website_url: website.trim(),
        cities: cities.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post(`/admin/product-requests/${request.id}/approve`, body);
      toast.success("Approved & product published");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-lg rounded-none border max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pr-approve-modal">
        <div className="flex justify-between px-5 py-4 border-b border-border">
          <h3 className="font-heading font-bold text-lg">Approve & publish</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="border border-border p-3 rounded-none bg-foreground/5">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">From request</div>
            <div className="font-heading font-bold mt-1">{request.product}</div>
            <div className="text-xs text-muted-foreground">{request.brand}</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Category *</label>
            <Combobox
              value={category}
              onChange={setCategory}
              options={categories.map((c) => ({ value: c.slug, label: c.label, hint: `${c.count} listings` }))}
              placeholder="Pick or create category"
              createLabel="Create new category"
              testId="pr-approve-category"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Primary Tags (comma-separated, up to 6)</label>
            <textarea rows={2} value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="e.g. Car, SUV, Luxury" className="input-bhega mt-1" data-testid="pr-approve-primary" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Variants & Tags</label>
            <textarea rows={2} value={variants} onChange={(e) => setVariants(e.target.value)} placeholder="e.g. xDrive20d, xDrive30i" className="input-bhega mt-1" data-testid="pr-approve-variants" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Original Website URL</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" className="input-bhega mt-1" data-testid="pr-approve-website" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase">Cities (comma-separated)</label>
            <textarea rows={2} value={cities} onChange={(e) => setCities(e.target.value)} placeholder="e.g. Mumbai, Maharashtra, Pune, Maharashtra" className="input-bhega mt-1" data-testid="pr-approve-cities" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="pr-approve-submit">{busy ? "Publishing…" : "Approve & publish"}</button>
        </div>
      </div>
    </div>
  );
}
